// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//

import React from "react";
// import { Button, Grid, ListItemIcon, Menu, MenuItem, Typography, withStyles, WithStyles } from "@material-ui/core";
//import FilterListIcon from '@material-ui/icons/FilterList';
import { Button, Grid, ListItemIcon, Menu, MenuItem, Typography, Tooltip } from "@mui/material";
import { withStyles } from "tss-react/mui";
import FilterListIcon from '@mui/icons-material/FilterList';
import { JOB_REQUEST_FILTER_LABELS, JOB_REQUEST_FILTERS } from "@shared/constants/job-request";
import { Assets, Staff, Orders, Map } from "@shared/icons/NavigationMenu";
import FlagIcon from "@shared/icons/Flag";
import TagIcon from '@shared/icons/Tag';
import FlagIcon2 from "@shared/icons/Flag2/Flag2";
import { action, makeObservable, observable } from "mobx";
import { FilterItems } from "@shared/models/jobrequesttimelines";
import { JobRequestFilter } from "@modules/Private/submodules/JobRequestTimelines/components/Filters";
import { observer } from "mobx-react";
import { clearPersistData } from "@shared/utils/common";
// import Tooltip from '@material-ui/core/Tooltip';
import { showNotification, NotificationType } from '@shared/components/Notification';
import Tasks from "@shared/components/Tasks";
import { OrderTask } from "@shared/models/ordertask";
import { Option } from '@shared/components/Select';
import CalendarMonth from "@shared/icons/CalendarMonth/CalendarMonth";

// Prop interface for main class.
export interface DBFilterProps {
    className?: string;
    orderId?: number;                                       // Order Id to query for asset list.
    assetList?: Array<{id: number, name: string, tag: string}>; // List of assets for this order. Replacing call to this.orderStore.getAssetList(orderId) 
                                                                //   which may show assets not viewable for the current user.  TBD: also replace orderID (?).
    possibleFlags?: Array<Option>;                          // Values for flag selection box.
    staffIds?: Array<number>;                               // List of staff Ids for this order.
    filterConfig: Array<FilterItems>;                       // Filter info for each category (staff, asset, ...).
    filterSelections: number[];                             // List flags (either 0 or 1) to keep track of which categories are active.
    doFilter: (arg0: FilterItems) => any;                   // callback to do the filtering
    isProjectManager: boolean;
}


// Class to show the Filter menu and handle user selections.
//   Staff
//   Site
//   Workflow
//   Status
//   Tag
//   Flag
//   Plan end
@observer
class DBFilter extends React.Component<DBFilterProps> {
    @observable showFilterUsers: boolean;                  // Flag to show filtering by staff.
    @observable showFilterAssetIds: boolean;               // Flag to show filtering by assetIds (using List).
    @observable showFilterAssetIdsByMap: boolean;          // Flag to show filtering by assetIds (using Map).
    @observable showFilterTasks: boolean;                  // Flag to show filtering by tasks.
    @observable showFilterStatuses: boolean;               // Flag to show filtering by status.
    @observable showFilterTag: boolean;                    // Flag to show filtering by tag.
    @observable showFilterFlag: boolean;                   // Flag to show filtering by flag.
    //@observable showFilterPlatforms: boolean;            // Flag to show filtering by platform.
    @observable showFilterPlannedDate: boolean;            // Flag to show filtering by planned date.

    state = {
        anchorEl: null  // anchor element to post up the menu.
    };

    // Array for menu items.  Changes in ids/order here must be synced to SPContainer::initFilterConfig(), and job-request.ts::JOB_REQUEST_FILTERS
    private menuItems = [ 
        {
            id: JOB_REQUEST_FILTERS.staff,
            icon: <Staff />,
        },
        {
            id: JOB_REQUEST_FILTERS.assets,
            icon: <Assets />,
        },
        {
            id: JOB_REQUEST_FILTERS.task,
            icon: <Orders />
        },
        {
            id: JOB_REQUEST_FILTERS.status,
            icon: <FlagIcon />,
        },
        {
            id: JOB_REQUEST_FILTERS.tag1,
            icon: <TagIcon />,
        },
        {
            id: JOB_REQUEST_FILTERS.flag,
            icon: <FlagIcon2 />,
        },
        // {
        //     id: JOB_REQUEST_FILTERS.platforms,
        //     icon: <Platforms />,
        // },
        {
            id: JOB_REQUEST_FILTERS.planned,
            icon: <CalendarMonth />
        },
        {
            id: JOB_REQUEST_FILTERS.assetsByMap,
            icon: <Map />,
        }
    ];

    constructor(props) {
        super(props);

        this.showFilterUsers = false;       // ie. Staff.
        this.showFilterAssetIds = false;    // ie. Sites.
        this.showFilterAssetIdsByMap = false;
        this.showFilterTasks = false;
        this.showFilterStatuses = false;    
        this.showFilterTag = false;
        this.showFilterFlag = false;
        //this.showFilterPlatforms = false;
        this.showFilterPlannedDate = false;
        makeObservable(this);
    }

    // abc123 => abc
    private removeTrailingDigits = (string) =>  {
        return string.replace(/\d+$/, '');
    }

    // Show the menu when use clicks on the 'Filters' button.
    private handleClick = (event) => {
        this.setState( {anchorEl: event.currentTarget });
    }

    // Set flag to show the filter dialog corresponding to the selected id.
    @action handleSelect = (event) => {
        let { id } = event.currentTarget
        //console.log("*** handleSelect: ", id);
        if (id === JOB_REQUEST_FILTERS.staff && this.props.isProjectManager == false) {
            showNotification('Only a project manager can filter by staff', NotificationType.error);
            return;
        }
        if (id === JOB_REQUEST_FILTERS.status) {
            id = JOB_REQUEST_FILTERS.priority;  // Display label is status but internal filed is a combination of priority and underlying tasks' status
        }

        // Close menu.
        this.setState( {anchorEl: null });

        if(id) {
            // Clear all flags to close any open dialog.
            this.showFilterUsers = false;
            this.showFilterAssetIds = false;
            this.showFilterAssetIdsByMap = false;
            this.showFilterTasks = false;
            this.showFilterStatuses = false;
            this.showFilterTag = false;
            this.showFilterFlag = false;
            //this.showFilterPlatforms = false;
            this.showFilterPlannedDate = false;

            // Turn on the flag corresponding to the selected menu item.
            if(id === JOB_REQUEST_FILTERS.staff) {
                this.showFilterUsers = true;
            }
            else if(id === JOB_REQUEST_FILTERS.assets) {
                this.showFilterAssetIds = true;
            }
            else if(id === JOB_REQUEST_FILTERS.assetsByMap) {
                this.showFilterAssetIdsByMap = true;
            }
            else if(id === JOB_REQUEST_FILTERS.task) {
                this.showFilterTasks = true;
            }
            else if(id === JOB_REQUEST_FILTERS.priority) {
                this.showFilterStatuses = true;
            }
            else if(id === JOB_REQUEST_FILTERS.tag1) {
                this.showFilterTag = true;
            }
            else if(id === JOB_REQUEST_FILTERS.flag) {
                this.showFilterFlag = true;
            }
            // else if(id === JOB_REQUEST_FILTERS.platforms) {
            //     this.showFilterPlatforms = true;
            // }
            else if(id === JOB_REQUEST_FILTERS.planned) {
                this.showFilterPlannedDate = true;
            }
        }
    }

    // Post filter items to parent obj to reload data.
    @action handleFilter = (items: FilterItems) => {
        const { doFilter } = this.props;
        // Clear all flags to close any open dialog.
        this.showFilterUsers = false;
        this.showFilterAssetIds = false;
        this.showFilterAssetIdsByMap = false;
        this.showFilterTasks = false;
        this.showFilterStatuses = false;
        this.showFilterTag = false;
        this.showFilterFlag = false;
        //this.showFilterPlatforms = false;
        this.showFilterPlannedDate = false;

        // Bubble up request to apply filters.
        const { type } = items;
        if (type !== 'none') {
            clearPersistData(); // Maybe should be done by parent.
            // Call parent's method to filter DB records.
            doFilter(items);
        }
    }

    // Reuse the Task component which has a different submission format.
    // Reformat to match the format for this component.
    private handleSubmitTasks = (selectedDelis: OrderTask[]) => {

        const ids = (selectedDelis && selectedDelis.length > 0) ? selectedDelis.map(d => d.delTypeId) : [];
        const items: FilterItems = {
            type: JOB_REQUEST_FILTERS.task,
            ids: ids,
            keywords: ''
        }

        this.handleFilter(items);
    }
    
    @action handleCloseTasks = async () => {
        this.showFilterTasks = false;
    }
    
    // Return the dialog element for the filter to be displayed.
    showFilters = () => {
        const { orderId, filterConfig, possibleFlags, staffIds, assetList } = this.props;

        const selectedIds: Array<OrderTask> = filterConfig[2].ids ?  filterConfig[2].ids.map(i => ({delTypeId: Number(i), isInternal: false})): [];

        // Return a filter if its flag is set.
        return (
          <Grid container spacing={0}>
            {this.showFilterUsers && (  // Staff filter.
            <Grid item xs={12}>
              <div>
                <JobRequestFilter onFilter={this.handleFilter.bind(this)} filterInfo={filterConfig[0]} staffIds={staffIds}/>
              </div>
            </Grid>)         
            }

            {this.showFilterAssetIds && (  // Asset filter.
            <Grid item xs={12}>
              <div>
                <JobRequestFilter onFilter={this.handleFilter.bind(this)} orderId={orderId} assetList={assetList} filterInfo={filterConfig[1]} />
              </div>
            </Grid>)         
            }

            {this.showFilterAssetIdsByMap && (  // Asset filter.
            <Grid item xs={12}>
              <div>
                <JobRequestFilter onFilter={this.handleFilter.bind(this)} orderId={orderId} assetList={assetList} filterInfo={filterConfig[7]} />
              </div>
            </Grid>)         
            }

            {this.showFilterTasks && (  // Task filter.  Note: use <Tasks> instead of <JobRequestFilter> like the others.
            <Grid item xs={12}>
              <div>
                <Tasks
                    orderTasks={selectedIds}
                    forFiltering={true}
                    onClose={this.handleCloseTasks.bind(this)}
                    onSubmit={this.handleSubmitTasks.bind(this)}
                />
              </div>
            </Grid>)         
            }

            {this.showFilterStatuses && (  // Status filter.
            <Grid item xs={12}>
              <div>
                <JobRequestFilter onFilter={this.handleFilter} filterInfo={filterConfig[3]} />
              </div>
            </Grid>)         
            }

            {this.showFilterTag && (  // Tag filter.
            <Grid item xs={12}>
              <div>
                <JobRequestFilter onFilter={this.handleFilter} orderId={orderId} filterInfo={filterConfig[4]} />
              </div>
            </Grid>)         
            }

            {this.showFilterFlag && (  // Flag filter.
            <Grid item xs={12}>
            <div>
                <JobRequestFilter onFilter={this.handleFilter.bind(this)} filterInfo={filterConfig[5]} possibleFlags={possibleFlags}/>
            </div>
            </Grid>)         
            }

            {/* {this.showFilterPlatforms && (  // Platform filter.
            <Grid item xs={12}>
              <div>
                <JobRequestFilter onFilter={this.handleFilter.bind(this)} filterInfo={filterConfig[6]} />
              </div>
            </Grid>)         
            } */}

            {this.showFilterPlannedDate && (  // Planned Date dialog.
            <Grid item xs={12}>
              <div>
                <JobRequestFilter onFilter={this.handleFilter.bind(this)} filterInfo={filterConfig[6]} />
              </div>
            </Grid>)         
            }

        </Grid>   
        );  
    }

    render() {
        const { filterConfig } = this.props;
        const classes = withStyles.getClasses(this.props);
        // console.log("****** render DBFilter");

        // Set highlight color for menu text if any filter is active.
        const filterOn = filterConfig.some(item => item.ids && item.ids.length > 0);

        return (
            <div>
                { /* Show the 'Filters' link/button */}
                <Tooltip title='Database query filters'>
                    <Button
                        classes={{root: filterOn ? classes.mapFilterButton : classes.mapFilterButtonAlt}}
                        endIcon={<FilterListIcon />}
                        onClick={this.handleClick.bind(this)}
                        size="large"
                        color="primary"
                    >
                        Filters
                    </Button>
                </Tooltip>

                { /* Show the floating menu when user click on the button above */}
                <Menu
                    id="filter-menu"
                    anchorEl={this.state.anchorEl}
                    keepMounted
                    open={Boolean(this.state.anchorEl)}
                    onClose={this.handleSelect.bind(this)}
                >
                {this.menuItems.map(({id, icon}, idx) => (
                    <MenuItem id={id} onClick={this.handleSelect.bind(this)} key={id}>
                        <ListItemIcon classes={{root: this.props.filterSelections[idx] !== 0 ? classes.mapFilterOption : classes.mapFilterOptionAlt}} >
                            {icon}
                        </ListItemIcon>
                        <Typography variant="inherit" className={this.props.filterSelections[idx] !== 0 ? classes.menuTextActive : ''} >{ this.removeTrailingDigits(JOB_REQUEST_FILTER_LABELS[id]) }</Typography>
                    </MenuItem>
                ))}
                </Menu> 

                {   // Show a filter Dialog corresponding the menu item seledted by the user. 
                    this.showFilters()
                }
            </div>
        );
    }
}

const DBFilterStyled = withStyles(
    DBFilter, 
    (theme, props) => ({
        "root": { },
        /* Set style (color) for active/inactive filter button */
        "mapFilterButton": {
            fontWeight: 'normal',
            // borderRadius: '0%',
            color: '#0870d8',
            '&:hover': {
                backgroundColor: '#daedff80'
            },
            "&.MuiButton-text": { 
                color: theme.palette.colors.primaryAccent
            },
        },
        "mapFilterButtonAlt": {
            fontWeight: 'normal',
            // borderRadius: '0%',
            color: '#364859',
            '&:hover': {
                backgroundColor: '#daedff80'
            }
        },
    
        /* Set style (color) for active/inactive filter items */
        "mapFilterOption": {
            color: '#0870d8',
            fill: '#0870d8',
            opacity: 0.8,
            minWidth: '30px',
            '& svg g': {
                opacity: 0.8,
                color: theme.palette.colors.primaryAccent,
            }
        },
        "mapFilterOptionAlt": {
            color: '#000000',
            fill: '#000000',
            opacity: 0.8,
            minWidth: '30px',
            '& svg g': {
                opacity: 0.34
            }
        },
        "menuTextActive": {
            color: theme.palette.colors.primaryAccent,
        },
    })
);
  
export default DBFilterStyled;